import {
    Component,
    Element,
    forceUpdate,
    h,
    Host,
    Listen,
    Prop,
    State,
} from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";
import { track } from "../../utils/tracking";

const scrollAreaMargin: number = 45;

/**
 * @slot headline - Slot for the headline (`<h3 class="ux-iws-nb-filter__headline">`).
 */
@Component({
    tag: "ux-iws-nb-filter",
    styleUrl: "nb-filter.scss",
})
export class NbFilter {
    private i18nSubscriberId: string;
    private scrollInner: HTMLDivElement;
    private container: HTMLDivElement;

    @Element() element: HTMLUxIwsNbFilterElement;

    /**
     * The label for the close button in the dropdowns.
     * This will be used to set the `data-close-label` attribute on all `ux-dropdown` elements within this component.
     */
    @Prop() closeLabel: string = "Close";
    /** The label for the scroll left button */
    @Prop() scrollLeftLabel: string = "Scroll left";
    /** The label for the scroll right button */
    @Prop() scrollRightLabel: string = "Scroll right";

    @State() scrollPosition: number = 0;
    @State() windowWidth: number = window.innerWidth;
    @State() scrollAreaWidth: number = 0;
    @State() hasActiveFilter: boolean = false;
    @State() isSticky: boolean = false;

    connectedCallback() {
        this.element.querySelectorAll("ux-dropdown").forEach((dropdown) => {
            dropdown.setAttribute("data-close-label", this.closeLabel);
        });
        this.element.addEventListener("click", (event) => {
            const target = event.target as HTMLElement;
            if (target.classList.contains("ux-dropdown__label")) {
                setTimeout(this.handleViewportChange, 50);
            }
        });
        window.addEventListener("resize", this.handleViewportChange);
        window.addEventListener("scroll", this.updateStickyState);
        this.handleViewportChange();
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
    }

    disconnectedCallback() {
        window.removeEventListener("resize", this.handleViewportChange);
        window.removeEventListener("scroll", this.updateStickyState);
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    componentDidLoad() {
        this.insertCloseIcons();
        window.setTimeout(() => this.updateStickyState(), 0);
    }

    private handleViewportChange = () => {
        this.updateWindowWidth();
        this.updateScrollAreaWidth();
        this.updateScrollButtons();
        if (this.isSticky && this.container) {
            const filterRect = this.container.getBoundingClientRect();
            this.element.style.setProperty(
                "--filter-height",
                `${filterRect.height}px`
            );
        }
    };

    private updateStickyState = () => {
        const rect = this.element.getBoundingClientRect();
        const isSticky = rect.top <= 0;
        if (this.isSticky !== isSticky) {
            this.isSticky = isSticky;
            if (isSticky) {
                this.element.setAttribute("is-sticky", "");
                const filterRect =
                    this.container?.getBoundingClientRect() || rect;
                this.element.style.setProperty(
                    "--filter-height",
                    `${filterRect.height}px`
                );
            } else {
                this.element.removeAttribute("is-sticky");
            }
        }
    };

    private insertCloseIcons() {
        this.element.querySelectorAll("ux-dropdown").forEach((dropdown) => {
            const closeIcon = document.createElement("ux-icon");
            closeIcon.setAttribute("name", "cross");
            closeIcon.className = "ux-iws-nb-filter-dropdown__close-icon";
            dropdown.insertAdjacentElement("afterend", closeIcon);
        });
    }

    @Listen("uxFilterChange")
    updateScrollAreaWidth() {
        let width = scrollAreaMargin; // Initial width for the scroll buttons
        const scrollItems = this.element.querySelectorAll("ux-form-group");
        scrollItems.forEach((item) => {
            const itemWidth = item.getBoundingClientRect().width;
            width += itemWidth;
        });
        this.scrollAreaWidth = Math.floor(width);
        this.element.style.setProperty(
            "--ux-iws-nb-filter-form-width",
            `${width}px`
        );
    }

    private updateWindowWidth() {
        this.windowWidth = window.innerWidth;
    }

    @Listen("uxFilterChange")
    trackFilter() {
        // currently this only works with ux-checkboxes (e.g. ux-checkbox-list-item)
        const filterData: { [key: string]: string } = {};
        Array.from(this.scrollInner.querySelectorAll("ux-checkbox"))
            .filter((checkbox) => checkbox.checked)
            .forEach((checkbox) => {
                const { name, value } = checkbox;
                if (name && value) {
                    const oldValue = filterData[name];
                    filterData[name] = oldValue
                        ? `${oldValue},${value}`
                        : value;
                }
            });
        this.hasActiveFilter = Object.keys(filterData).length > 0;
        track("change_filter", { filters: filterData });
    }

    @Listen("uxFilterChange")
    updateScrollButtons() {
        if (!this.scrollInner) {
            return;
        }
        this.scrollPosition = this.scrollInner.scrollLeft;
    }

    private scroll(direction: "left" | "right") {
        const distance = this.scrollInner.clientWidth - scrollAreaMargin;
        this.scrollInner.scrollBy({
            left: direction === "left" ? -distance : distance,
            behavior: "smooth",
        });
        this.updateScrollButtons();
    }

    private removeAllFilters = () => {
        const filterRemoveAllButton = this.element.querySelector(
            ".ux-filter__remove-all"
        ) as HTMLElement;
        if (filterRemoveAllButton) {
            filterRemoveAllButton.click();
        }
    };

    render() {
        const { t } = i18nProvider;
        const baseClass = "ux-iws-nb-filter";
        const hasRemoveAllButton = !!this.element.querySelector(
            ".ux-filter__remove-all"
        );
        return (
            <Host
                active-filter={this.hasActiveFilter ? "" : undefined}
                is-sticky={this.isSticky ? "" : undefined}
            >
                <div
                    class={`${baseClass}__placeholder`}
                    role="presentation"
                    aria-hidden="true"
                />
                <div
                    class={`${baseClass}__container`}
                    ref={(el) => (this.container = el)}
                >
                    <header class={`${baseClass}__header`}>
                        <slot name="headline" />
                    </header>
                    <div class={`${baseClass}__scroll-outer`}>
                        <button
                            aria-label={this.scrollLeftLabel}
                            class={{
                                [`${baseClass}__scroll-button ${baseClass}__scroll-button--left`]: true,
                                "is-overlapping": this.scrollPosition > 0,
                                "is-deactivated": this.scrollPosition === 0,
                                "is-hidden":
                                    this.windowWidth >= this.scrollAreaWidth,
                            }}
                            onClick={() => this.scroll("left")}
                        >
                            <ux-icon name="chevron-left"></ux-icon>
                        </button>
                        <div
                            class={`${baseClass}__scroll-inner`}
                            ref={(el) =>
                                (this.scrollInner = el as HTMLDivElement)
                            }
                            onScroll={() => {
                                this.updateScrollButtons();
                            }}
                        >
                            <slot />
                        </div>
                        <button
                            aria-label={this.scrollRightLabel}
                            class={{
                                [`${baseClass}__scroll-button ${baseClass}__scroll-button--right`]: true,
                                "is-overlapping": this.scrollInner
                                    ? this.scrollPosition +
                                          this.scrollAreaWidth <
                                      this.windowWidth
                                    : true,
                                "is-deactivated":
                                    this.scrollPosition + this.windowWidth >=
                                    this.scrollAreaWidth + scrollAreaMargin,
                                "is-hidden":
                                    this.windowWidth >= this.scrollAreaWidth,
                            }}
                            onClick={() => this.scroll("right")}
                        >
                            <ux-icon name="chevron-right"></ux-icon>
                        </button>
                    </div>
                    {hasRemoveAllButton && (
                        <div class={`${baseClass}__remove-all`}>
                            <button
                                type="button"
                                onClick={this.removeAllFilters}
                            >
                                {t("filter.clearAll")}
                                <ux-icon name="close"></ux-icon>
                            </button>
                        </div>
                    )}
                </div>
            </Host>
        );
    }
}
